<script setup lang="ts">
import type { Submenu } from '@global-types/nav-v2/menuItems';
import MenuList from '@layout/nav-v2/desktop/components/MenuList.vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconBuildings from '@icons/ph_buildings.svg';
import IconTrendUp from '@icons/ph_trend-up.svg';
import IconReceip from '@icons/ph_receip.svg';
import IconBriefCase from '@icons/ph_briefcase.svg';
import Icon from '@components/Icon.vue';
import Divider from '@components/Divider.vue';
import MenuLink from '@layout/nav-v2/desktop/components/MenuLink.vue';
import MenuItemGroup from '@layout/nav-v2/desktop/components/MenuItemGroup.vue';
import MenuAccountManagerContact from '@layout/nav-v2/MenuAccountManagerContact.vue';
import { trans } from '@elements/translation.ts';
import {
  URL_ADAF,
  URL_ADDRESS_PAGE,
  URL_INVOICES,
  URL_MANAGEMENT_INVOICES,
  URL_PAYOUTS,
  URL_SELLER_ORGANIZATION_MANAGE,
  URL_SUBSCRIPTION,
  URL_USER_INBOX,
  URL_USER_LEADS,
  URL_USER_OFFERS,
  URL_USER_ORGANIZATION_PAYMENT_ACCOUNT,
  URL_USER_ORGANIZATION_PAYMENT_METHOD_SELLER,
  URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROFILE_INVOICE,
} from '@elements/tools/routes/routes';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';

const navbarStore = useNavbarStore();
const { isExpanded, notifications } = storeToRefs(navbarStore);
const { setCurrentDropdownOpen, isCurrentDropdownOpen } = navbarStore;

const { user, hasKYCActive, hasPaySeller } = storeToRefs(useUserProfileStore());

const organizationSubmenus = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.organization'), link: URL_SELLER_ORGANIZATION_MANAGE },
    { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
    { name: trans('user_menu.adaf_invoices'), link: URL_USER_PROFILE_INVOICE },
  ];

  if (hasKYCActive.value) {
    items.push(
      { name: trans('user_menu.invoices.management'), link: URL_MANAGEMENT_INVOICES },
      { name: trans('user_menu.gestion_parameters'), link: URL_USER_ORGANIZATION_PAYMENT_METHOD_SELLER },
    );
  }

  return items;
});

const invoicingSubmenus = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.subscriptions'), link: URL_SUBSCRIPTION },
    { name: trans('user_menu.pay_invoices'), link: URL_INVOICES },
    { name: trans('user_menu.payouts'), link: URL_PAYOUTS },
  ];

  if (hasPaySeller.value) {
    items.push({ name: trans('user_menu.invoices.settings'), link: URL_USER_ORGANIZATION_PAYMENT_ACCOUNT });
  }

  return items;
});
</script>

<template>
  <MenuList>
    <li>
      <MenuLink
        :link="URL_USER_INBOX"
        :is-expanded="isExpanded"
        :has-pin="notifications.unread_messages > 0"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconEnvelope"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.inbox') }}

          <template v-if="notifications.unread_messages > 0">
            ({{ notifications.unread_messages }})
          </template>
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuItemGroup
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.PROPERTY_HOLDINGS)"
        :is-expanded="isExpanded"
        :submenus="[
          { name: trans('user_menu.offers'), link: URL_USER_OFFERS },
          { name: trans('user_menu.spaces'), link: URL_ADDRESS_PAGE },
        ]"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.PROPERTY_HOLDINGS : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconBuildings"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.property_holdings') }}
        </template>
      </MenuItemGroup>
    </li>

    <li>
      <MenuItemGroup
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.SALES)"
        :is-expanded="isExpanded"
        :submenus="[
          { name: trans('user_menu.leads'), link: URL_USER_LEADS },
          { name: trans('user_menu.adaf'), link: URL_ADAF },
        ]"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.SALES : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconTrendUp"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.sales') }}
        </template>
      </MenuItemGroup>
    </li>

    <li>
      <MenuItemGroup
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.INVOICING)"
        :is-expanded="isExpanded"
        :has-new-tag="!hasKYCActive"
        :submenus="invoicingSubmenus"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.INVOICING : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconReceip"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.invoicing') }}
        </template>
      </MenuItemGroup>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <MenuItemGroup
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
        :is-expanded="isExpanded"
        :submenus="organizationSubmenus"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconBriefCase"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user.menu.my_organization') }}
        </template>
      </MenuItemGroup>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li v-if="isExpanded">
      <MenuAccountManagerContact
        class="mx-3"
        :account-manager="user!.account_manager"
      />
    </li>
  </MenuList>
</template>
